import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TemporaryChatContext } from '../Context/temporaryChatContext';
import chatService from '../services/chatService';
import sessionsService from '../services/sessionsServices';
import { processChatStream } from '../utils/processChatStream';

export function useChatSubmit({ createSession = false, sessionId } = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    updateUserChat,
    webSearch,
    updateBotChat,
    setChatmessages,
    setSessionID,
    updateChatSource,
    selectedAgent,
    sessionAgent,
    updateAgent,
    setSessionAgent,
    toggleRef,
    fakeAiLoader,
    fakeAiLoaderClear,
    updateChatLoaderMessage,
    updateToolImages,
  } = useContext(TemporaryChatContext);

  const getActiveAgent = () => (selectedAgent?.value ? selectedAgent : sessionAgent);

  const mutationCreateSession = useMutation({
    mutationFn: () => {
      const agent = getActiveAgent();
      return sessionsService.createSession({
        platform_id: 'chronolabs',
        agent_id: agent?.value,
        project_id: 'test',
        mode: agent?.mode,
      });
    },
  });

  const streamHandlers = {
    updateChatLoaderMessage,
    updateBotChat,
    updateToolImages,
    updateChatSource,
  };

  const submitMessage = async text => {
    const query = text?.trim();
    if (!query || isSubmitting) return false;

    // Only the URL session id is authoritative — never fall back to a stale context session.
    if (!createSession && !sessionId) {
      return false;
    }

    const agent = getActiveAgent();
    if (!agent?.value) return false;

    setIsSubmitting(true);
    setLoading(true);
    toggleRef(false);

    try {
      if (createSession) {
        setChatmessages([]);
        updateAgent(agent);
        setSessionAgent(agent);
        const sessionRes = await mutationCreateSession.mutateAsync();
        queryClient.invalidateQueries({ queryKey: ['sidebar_sessions'] });

        setSessionID(sessionRes?.session_id);
        updateUserChat(query);
        navigate(`/chat/${sessionRes?.session_id}`);

        fakeAiLoader();
        chatService.generateTitle({
          session_id: sessionRes?.session_id,
          query,
        });

        const aiResp = await chatService.createChat({
          platform_id: 'chronolabs',
          agent_id: agent.value,
          session_id: sessionRes?.session_id,
          mode: agent.mode,
          query,
          use_google_search: webSearch,
        });

        setLoading(false);
        await processChatStream(aiResp.body.getReader(), streamHandlers);
      } else {
        updateUserChat(query);
        fakeAiLoader();

        const aiResp = await chatService.createChat({
          platform_id: 'chronolabs',
          agent_id: agent.value,
          session_id: sessionId,
          query,
          mode: agent.mode,
          use_google_search: webSearch,
        });

        setLoading(false);
        await processChatStream(aiResp.body.getReader(), streamHandlers, { botChatSeparator: '\n \n' });
      }

      return true;
    } catch (error) {
      fakeAiLoaderClear();
      setLoading(false);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submitMessage, isSubmitting, loading };
}
